import http from '../http'

const BASE = '/products'

/**
 * 将 API 返回的 specModel 转为组件使用的 specification
 */
function toComponentProduct(item) {
  if (!item) return null
  const { specModel, ...rest } = item
  return { ...rest, specification: specModel }
}

/**
 * 将组件的 product 转为 API 请求体（specification -> specModel，不包含 id）
 */
function toApiProduct(product) {
  if (!product) return {}
  const { specification, ...rest } = product
  delete rest.id
  return { ...rest, specModel: specification }
}

/**
 * 分页查询产品列表
 * @param {Object} params - 查询参数
 * @param {string} [params.productName] - 产品名称（模糊查询）
 * @param {string} [params.specModel] - 规格型号（模糊查询），组件传 specification 时需映射
 * @param {number} [params.page=0] - 页码
 * @param {number} [params.size=10] - 每页条数
 * @param {string} [params.sortBy=id] - 排序字段
 * @param {string} [params.sortDir=desc] - 排序方向 asc/desc
 * @returns {Promise} 返回分页数据 { content, totalElements, totalPages, size, number }
 */
export function queryProductsPagination(unitId, params = {}) {
  const queryParams = {
    productName: params.productName || undefined,
    specModel: params.specification ?? params.specModel,
    page: params.page !== undefined ? params.page : 0,
    size: params.size !== undefined ? params.size : 10,
    sortBy: params.sortBy || 'id',
    sortDir: params.sortDir || 'desc'
  }
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined || queryParams[key] === '') delete queryParams[key]
  })
  return http.get(`${BASE}/page`, { params: queryParams }).then(res => {
    const data = res.data?.data
    if (data && data.content) {
      data.content = data.content.map((item, i) => {
        const row = toComponentProduct(item)
        row.sequenceNumber = (data.number || 0) * (data.size || 10) + i + 1
        return row
      })
    }
    return res
  })
}

/**
 * 根据 ID 获取产品
 * @param {number|string} id - 产品 ID
 * @returns {Promise}
 */
export function getProductById(id) {
  return http.get(`${BASE}/${id}`).then(res => {
    if (res.data?.data) {
      res.data.data = toComponentProduct(res.data.data)
    }
    return res
  })
}

/**
 * 新增产品
 * @param {Object} product - 产品数据（unit_info_id 由后端从当前用户自动获取）
 * @returns {Promise}
 */
export function createProduct(product) {
  return http.post(BASE, toApiProduct(product))
}

/**
 * 更新产品
 * @param {number|string} id - 产品 ID
 * @param {Object} product - 产品数据
 * @returns {Promise}
 */
export function updateProduct(id, product) {
  return http.put(`${BASE}/${id}`, toApiProduct(product))
}

/**
 * 保存产品（新增或更新）
 * @param {number|string} unitId - 单位 ID（兼容旧调用，实际由后端自动获取）
 * @param {Object} product - 产品数据
 * @returns {Promise}
 */
export function saveProduct(unitId, product) {
  if (product.id) {
    return updateProduct(product.id, product)
  }
  return createProduct(product)
}

/**
 * 删除产品（逻辑删除）
 * @param {number|string} unitId - 单位 ID（兼容旧调用）
 * @param {number|string} productId - 产品 ID
 * @returns {Promise}
 */
export function deleteProduct(unitId, productId) {
  return http.delete(`${BASE}/${productId}`)
}

/**
 * 导入产品（Excel/CSV）
 * @param {File} file - Excel (.xlsx) 或 CSV 文件
 * @returns {Promise} { successCount, failCount, failDetails }
 */
export function importProducts(file) {
  const formData = new FormData()
  formData.append('file', file)
  return http.post(`${BASE}/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}
